import React, { useState, useEffect } from 'react';
import SoftPhone from '../softphone';
import './style.css';
import { useAuth } from '../../../../app/modules/auth';
import { 
  Drawer, Fab, Dialog, DialogTitle, DialogContent, DialogActions, 
  Button, TextField, Box, Typography, IconButton, Paper, Divider, 
  InputAdornment
} from '@mui/material';
import { 
  Phone as PhoneIcon, 
  Close as CloseIcon, 
  Settings as SettingsIcon, 
  Save as SaveIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Dns as DnsIcon
} from '@mui/icons-material';

interface PhoneProps {
  children?: React.ReactNode;
}

function Phone({ children }: PhoneProps) {
  const { currentUser } = useAuth();
  
  const [callVolume, setCallVolume] = useState(() => {
    const saved = localStorage.getItem('softphone-call-volume');
    return saved ? parseFloat(saved) : 0.8;
  });
  
  const [ringVolume, setRingVolume] = useState(() => {
    const saved = localStorage.getItem('softphone-ring-volume');
    return saved ? parseFloat(saved) : 0.6;
  });
  
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('softphone-notifications');
    return saved ? JSON.parse(saved) : true;
  });
  
  const [connectOnStart, setConnectOnStart] = useState(() => {
    const saved = localStorage.getItem('softphone-connect-on-start');
    return saved ? JSON.parse(saved) : false;
  });

  const [telephonyMode, setTelephonyMode] = useState<'desktop' | 'web'>(() => {
    return (localStorage.getItem('telephony-device-mode') as 'desktop' | 'web') || 'desktop';
  });

  const [softPhoneOpen, setSoftPhoneOpen] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);

  const [manualConfig, setManualConfig] = useState(() => {
    const saved = localStorage.getItem('softphone-manual-config');
    return saved ? JSON.parse(saved) : null;
  });

  const [formData, setFormData] = useState({
    domain: process.env.REACT_APP_SIP_DOMAIN || '',
    extension: '',
    password: '',
    ws_servers: process.env.REACT_APP_SIP_WS_SERVER || '',
    display_name: ''
  });

  useEffect(() => {
    if (manualConfig) {
      setFormData(manualConfig);
      setIsConfigured(true);
    } else if (currentUser) {
      setFormData({
        domain: process.env.REACT_APP_SIP_DOMAIN || '',
        extension: currentUser.extension?.toString() ?? '',
        password: currentUser.password?.toString() ?? '',
        ws_servers: process.env.REACT_APP_SIP_WS_SERVER || '',
        display_name: currentUser.name?.toString() ?? ''
      });
      if (currentUser.extension && currentUser.password) {
        setIsConfigured(true);
      }
    }

    const toggleHandler = () => setSoftPhoneOpen(prev => !prev);
    const modeHandler = (e: any) => {
      if (e?.detail) setTelephonyMode(e.detail);
    };

    window.addEventListener('toggle-softphone', toggleHandler);
    window.addEventListener('telephony-device-mode-changed', modeHandler);
    return () => {
      window.removeEventListener('toggle-softphone', toggleHandler);
      window.removeEventListener('telephony-device-mode-changed', modeHandler);
    };
  }, [manualConfig, currentUser]);

  const sipConfig = {
    domain: formData.domain,     
    uri: `sip:${formData.extension}@${formData.domain}`, 
    password: formData.password,       
    ws_servers: formData.ws_servers, 
    display_name: formData.display_name,     
    realm: 'asterisk',
    register: telephonyMode === 'web',
    session_timers: false,
  };

  const handleSaveConfig = () => {
    localStorage.setItem('softphone-manual-config', JSON.stringify(formData));
    setManualConfig(formData);
    setIsConfigured(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('softphone-manual-config');
    setManualConfig(null);
    setIsConfigured(false);
    setSoftPhoneOpen(false);
  };

  // We can use a key prop to remount Softphone when config or mode changes
  const softPhoneKey = `${formData.extension}-${formData.domain}-${telephonyMode}`;

  return (
    <>
      {!isConfigured ? (
        <>
          {children}
          {/* FAB removed, triggered via Navbar instead */}
          <Drawer
            anchor="right"
            open={softPhoneOpen}
            onClose={() => setSoftPhoneOpen(false)}
            sx={{ 
              '& .MuiDrawer-paper': { 
                width: '360px', 
                p: 4, 
                boxSizing: 'border-box',
                borderTopLeftRadius: 24,
                borderBottomLeftRadius: 24,
                boxShadow: '-10px 0 30px rgba(0,0,0,0.15)',
                backgroundColor: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(12px)',
                backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(240,245,255,0.7) 100%)',
              } 
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight="700" color="primary">Manual Login Softphone</Typography>
              <IconButton onClick={() => setSoftPhoneOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="body2" color="textSecondary" mb={3}>
              Please enter your SIP credentials to connect to the softphone manually.
            </Typography>

            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField
                label="Extension"
                size="small"
                fullWidth
                variant="outlined"
                value={formData.extension}
                onChange={(e) => setFormData({...formData, extension: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Password"
                type="password"
                size="small"
                fullWidth
                variant="outlined"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Display Name"
                size="small"
                fullWidth
                variant="outlined"
                value={formData.display_name}
                onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="Domain"
                size="small"
                fullWidth
                variant="outlined"
                value={formData.domain}
                onChange={(e) => setFormData({...formData, domain: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><DnsIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField
                label="WebSocket Server"
                size="small"
                fullWidth
                variant="outlined"
                value={formData.ws_servers}
                onChange={(e) => setFormData({...formData, ws_servers: e.target.value})}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><DnsIcon fontSize="small" /></InputAdornment>,
                }}
              />
              
              <Button 
                variant="contained" 
                color="primary" 
                fullWidth 
                startIcon={<SaveIcon />}
                onClick={handleSaveConfig}
                sx={{ 
                  mt: 3, 
                  py: 1.5, 
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '1rem',
                  boxShadow: '0 8px 16px rgba(0, 100, 255, 0.25)',
                  background: 'linear-gradient(135deg, #0061ff 0%, #60efff 100%)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 20px rgba(0, 100, 255, 0.35)',
                    background: 'linear-gradient(135deg, #0051d9 0%, #4bd9e6 100%)',
                  },
                  '&:disabled': {
                    background: '#e0e0e0',
                    transform: 'none',
                    boxShadow: 'none',
                  }
                }}
                disabled={!formData.extension || !formData.password || !formData.domain || !formData.ws_servers}
              >
                Save & Connect
              </Button>
            </Box>
          </Drawer>
        </>
      ) : (
        <SoftPhone
          key={softPhoneKey}
          softPhoneOpen={softPhoneOpen}
          setSoftPhoneOpen={setSoftPhoneOpen}
          callVolume={callVolume}
          ringVolume={ringVolume}
          connectOnStart={connectOnStart}
          notifications={notifications}
          config={sipConfig}
          setConnectOnStartToLocalStorage={(v: any) => { setConnectOnStart(v); localStorage.setItem('softphone-connect-on-start', v); }}
          setNotifications={(v: any) => { setNotifications(v); localStorage.setItem('softphone-notifications', v); }}
          setCallVolume={(v: any) => { setCallVolume(v); localStorage.setItem('softphone-call-volume', v); }}
          setRingVolume={(v: any) => { setRingVolume(v); localStorage.setItem('softphone-ring-volume', v); }}
          builtInLauncher={false}
          launcherPosition="bottom-left"
          launcherSize="medium"
          launcherColor="primary"
          asteriskAccounts={[]}
          timelocale="UTC"
          onLogout={handleLogout}
        >
          {children}
        </SoftPhone>
      )}
    </>
  );
}
export { Phone };